import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { faMinusCircle, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import AccordionPanel from '@components/common/app-components/AccordionPanel';
import {
    IEnrollee,
    IHistoricalEnrollment,
    IHistoricalEnrollmentsViewProps,
} from '../types/historicalEnrollment.types';
import PlanCardSection from '@modules/members/historical-applications/components/Enrollments/PlanCardSection';
import { AddressDetailsSection, EnrollmentIdsSection, MonthlyPaymentSection } from '@modules/members/historical-applications/components/Enrollments';
import { IPaymentItem } from '@modules/members/historical-applications/types';
import { getEnrolleeCount } from '@modules/members/historical-applications/utils';
import EnrolleeHistoryTable from './EnrolleeHistoryTable';

// ============================================================================
// Helper Functions
// ============================================================================

const getPaymentItems = (enrollment: IHistoricalEnrollment): IPaymentItem[] => [
    { labelId: 'member.enrollment_list.premiumhistory.premiumAmount', value: enrollment.monthlyPremium },
    { labelId: 'member.enrollment_list.premiumhistory.electedFederalAPTCAmount', value: enrollment.electedAptc },
    {
        labelId: 'member.enrollment_list.PremiumCredit',
        value: enrollment.electedStateSubsidy,
        dataTestId: 'state-subsidy-amount',
    },
    { labelId: 'historical.member.applications.napa', value: enrollment.appliedStateSubsidy2 },
    { labelId: 'member.enrollment_list.premiumhistory.netPremium', value: enrollment.netPremium, isTotal: true },
];

const HistoricalEnrollmentsView: React.FC<IHistoricalEnrollmentsViewProps> = ({
    data,
    selectedYear,
}) => {
    const intl = useIntl();

    if (!data || !data.enrollments || data.enrollments.length === 0) {
        return (
            <div className="text-center margin-top-2 padding-4">
                <p>
                    <FormattedMessage 
                        id="enrollments.no_historical_enrollments" 
                        values={{ year: selectedYear }} 
                    />
                </p>
            </div>
        );
    }

    return (
        <>
            {data.enrollments.map((enrollment, index) => {
                const enrolleeCount = getEnrolleeCount(
                    enrollment.enrolleeHistoryEntries || [],
                    (e: IEnrollee) => e.relationship
                );

                return (
                    <div key={enrollment.policyId ?? `enrollment-${index}`} data-testid={`enrollment-card-${enrollment.policyId ?? index}`}>
                        <div className="historical-enrollment__card-content">
                            <div className="historical-enrollment__container" data-testid={`enrollment-details-${index}`}>

                                <PlanCardSection 
                                    id={`enrollment-plan-card-section-mp-${index}`}
                                    enrollment={{
                                        planName: enrollment.planName,
                                        issuerName: enrollment.issuerName,
                                        enrollmentStatus: enrollment.enrollmentStatus,
                                        enrollmentStartDate: enrollment.enrollmentStartDate,
                                        enrollmentEndDate: enrollment.enrollmentEndDate,
                                    }}
                                    labels={{
                                        enrollmentStatusLabel: 'member.enrollment_list.premiumhistory.enrollmentStatus',
                                        effectiveDateLabel: 'member.enrollment_list.premiumhistory.effectiveDate',
                                    }}
                                />

                                {/* Monthly Payment & Enrollment IDs */}
                                <div className="padding-2 margin-bottom-3">
                                    <div className="grid-row grid-gap-6">
                                        <div className="grid-col-5 tablet:grid-col-6">
                                            <MonthlyPaymentSection
                                                id={`enrollment-monthly-payment-section-${index}`}
                                                titleLabelId="member.enrollment_list.premiumhistory.monthlyPayment"
                                                paymentItems={getPaymentItems(enrollment)}
                                            />
                                        </div>
                                        <div className="grid-col-5 tablet:grid-col-6">
                                            <EnrollmentIdsSection enrollment={{
                                                policyId: enrollment.policyId,
                                                cmsPlanId: enrollment.cmsPlanId,
                                            }} />
                                        </div>
                                    </div>
                                </div>


                                <div className="historical-additional-info">
                                    <AccordionPanel
                                        id="additionalInfo"
                                        ariaControlsId="additionalInfoPanel"
                                        header={
                                            <span className="display-flex flex-align-center text-bold color-link">
                                                {intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.additionnalInfo' })}
                                            </span>
                                        }
                                        isLeftToggle={true}
                                        expandedIcon={faMinusCircle}
                                        collapsedIcon={faPlusCircle}
                                        isHeaderClickable={true}
                                    >
                                        <AddressDetailsSection
                                            homeAddress={enrollment.homeAddress}
                                            mailingAddress={enrollment.mailingAddress}
                                            primaryTaxFilerNames={enrollment.primaryTaxFilerNames}
                                        />
                                        <section className="historical-enrollment__enrollees-section">
                                            <h6 className="historical-enrollment__enrollees-heading">
                                                <FormattedMessage 
                                                    id="enrollments.enrollees_count" 
                                                    values={{ ...enrolleeCount }} 
                                                />
                                            </h6>
                                            <div className="historical-enrollment__table-wrapper">
                                                <EnrolleeHistoryTable 
                                                    enrollees={enrollment.enrolleeHistoryEntries} 
                                                />
                                            </div>
                                        </section>
                                    </AccordionPanel>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export default HistoricalEnrollmentsView;
